import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { BehaviorSubject, mergeMap, of, ReplaySubject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ConfigService } from '../../../services/config.service';
import { SearchService } from '../../../services/search.service';
import { HlCurrencyPipe } from '../../../pipes/hl-currency.pipe';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-delivery-estimate',
  templateUrl: './delivery-estimate.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatIcon,
    MatTooltip,
    NgIf,
    NgxSkeletonLoaderModule,
    NgFor,
    AsyncPipe,
    HlCurrencyPipe
  ]
})
export class DeliveryEstimateComponent {
  distanceAndDuration$ = new ReplaySubject<{
    distance: number;
    duration: number;
  } | null>(1);

  @Input() set destination(value: string | null) {
    this.noPlaceSelected$.next(Boolean(value) === false);
    this.estimateSource.next(value);
  }

  @Output() estimateSelected = new EventEmitter<number>();
  estimateSource = new ReplaySubject<string | null>(1);
  loading = new BehaviorSubject(false);
  estimateError$ = new BehaviorSubject<string | null>(null);
  estimate$ = this.estimateSource.asObservable().pipe(
    tap(() => {
      this.loading.next(true);
      this.estimateError$.next(null);
    }),
    switchMap((input) => {
      if (input) {
        return this.getByPlaceId(input);
      }
      return of(null);
    }),
    tap((response) => {
      if (!response?.distance || !response?.duration) {
        this.distanceAndDuration$.next(null);
        this.estimateError$.next('Unable to calculate delivery estimates');
      } else {
        this.distanceAndDuration$.next(response as any);
      }
    }),
    mergeMap((response: any) => {
      if (response === null || !response?.distance || !response?.duration) {
        return of([]);
      }
      return this.directionsToEstimate(response);
    }),
    tap(() => this.loading.next(false))
  );

  private estimateConfig = this.configService.userConfig.pipe(
    map(
      (config) =>
        config.delivery_estimate_factors ?? {
          sm: { factor: 2, minimum: 20 },
          md: { factor: 2.3, minimum: 35 },
          lg: { factor: 3.3, minimum: 60 },
          method: 'duration',
          rounding: { nearest: 2.5, alwaysRoundUp: true }
        }
    )
  );

  noPlaceSelected$ = new BehaviorSubject(true);

  private currentLocation$ = this.configService.currentLocation;
  helpText =
    'These estimates are determined based on the distance / duration from the current location to the destination. Go to Settings to modify how these estimates are calculated.';

  constructor(
    private searchService: SearchService,
    private configService: ConfigService
  ) {}

  private getByPlaceId(place_id) {
    return this.currentLocation$.pipe(
      mergeMap((location) => {
        if (!location) {
          return of(null);
        }
        return this.searchService.directions(place_id, location.id);
      })
    );
  }

  private directionsToEstimate(response: {
    distance: number;
    duration: number;
  }) {
    return this.estimateConfig.pipe(
      map((config) => {
        if (config.method === 'duration') {
          return [
            {
              label: 'Small',
              value: this.getDeliveryEstiamte(
                response.duration,
                config.sm,
                config.rounding
              )
            },
            {
              label: 'Medium',
              value: this.getDeliveryEstiamte(
                response.duration,
                config.md,
                config.rounding
              )
            },
            {
              label: 'Large',
              value: this.getDeliveryEstiamte(
                response.duration,
                config.lg,
                config.rounding
              )
            }
          ];
        }
        return [
          {
            label: 'Small',
            value: this.getDeliveryEstiamte(
              response.distance,
              config.sm,
              config.rounding
            )
          },
          {
            label: 'Medium',
            value: this.getDeliveryEstiamte(
              response.distance,
              config.md,
              config.rounding
            )
          },
          {
            label: 'Large',
            value: this.getDeliveryEstiamte(
              response.distance,
              config.lg,
              config.rounding
            )
          }
        ];
      })
    );
  }

  private getDeliveryEstiamte(
    sourceValue: number,
    factorSettings: DeliveryEstimateFactor,
    roundingSettings: DeliveryEstimateConfig['rounding']
  ) {
    const { factor, minimum } = factorSettings;
    const { nearest, alwaysRoundUp } = roundingSettings;
    const estimate = Math.max(sourceValue * factor, minimum);
    const rounded = Math.round(estimate / nearest) * nearest;
    const roundedUp = Math.ceil(estimate / nearest) * nearest;
    return (alwaysRoundUp ? roundedUp : rounded) * 100;
  }
}

interface DeliveryEstimateConfig {
  sm: DeliveryEstimateFactor;
  md: DeliveryEstimateFactor;
  lg: DeliveryEstimateFactor;
  method: 'duration' | 'distance';
  rounding: { nearest: number; alwaysRoundUp: boolean };
}

interface DeliveryEstimateFactor {
  factor: number;
  minimum: number;
}
